import type { Activity, ICDFView, Operation } from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type {
  CogniteClientDependencies as FDMCogniteClientDependencies,
  Filters,
  Sort,
} from '@cognite/fdm-client';
import type {
  AggregatedNumberValue,
  AggregatedValueItem,
  ViewReference,
} from '@cognite/sdk';

import { getCdmViewReferencesFromModelVersion } from '../../providers/fdm-services/cdm-view-reference-utils';
import { isNodeRef } from '../view/utils/node-ref';

import type { IActivityService } from './activity-service-interface';

export type CogniteClientDependencies = FDMCogniteClientDependencies;

/**
 * Only AggregatedNumberValue carries a numeric `value`; the histogram variant
 * does not. Narrowing on the `count` aggregate lets us read `value` without
 * an `as` assertion.
 */
const isCountAggregate = (
  aggregate: AggregatedValueItem
): aggregate is AggregatedNumberValue => aggregate.aggregate === 'count';

const addSourceDataOnlyFilter = (filters?: Filters): Filters => ({
  and: [
    ...(filters ? [{ ...filters }] : []),
    {
      isNull: {
        property: 'cloneOf',
        isNull: true,
      },
    },
    {
      not: {
        equals: {
          property: 'source',
          eq: 'APP',
        },
      },
    },
  ],
});

export type APMClientDependencies = {
  activities: ICDFView<Activity>;
  operations: ICDFView<Operation>;
};

export class CdmActivityService extends FDMClient implements IActivityService {
  cogniteClient: CogniteClientDependencies;
  apmClient: APMClientDependencies;
  readonly assetActivitiesCardView: ViewReference;
  readonly instanceSpace: string;
  readonly assetInstanceSpace: string;

  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies,
    instanceSpace?: string,
    assetInstanceSpace?: string,
    assetActivitiesCardView?: ViewReference
  ) {
    super(client);
    this.cogniteClient = client;
    this.apmClient = apmClient;
    this.instanceSpace = instanceSpace ?? 'maintenance_orders_space_not_set';
    this.assetInstanceSpace =
      assetInstanceSpace ?? instanceSpace ?? 'maintenance_orders_space_not_set';
    this.assetActivitiesCardView = {
      ...(assetActivitiesCardView ??
        getCdmViewReferencesFromModelVersion().idmMaintenanceOrder),
      type: 'view',
    };
  }

  async getActivityDetails(filters: Filters) {
    const {
      items: [activity],
    } = await this.apmClient.activities.list({
      filters: addSourceDataOnlyFilter(filters),
    });
    return activity;
  }

  async getActivityView() {
    const response = await this.cogniteClient.views.retrieve([
      {
        externalId: this.apmClient.activities.viewReference.externalId,
        space: this.apmClient.activities.viewReference.space,
        version: this.apmClient.activities.viewReference.version,
      },
    ]);

    return response.items[0];
  }

  async getActivities(filters?: Filters, sort?: Sort, pageSize = 200) {
    const { items: activities } = await this.apmClient.activities.list({
      filters: addSourceDataOnlyFilter(filters),
      sort,
      pageSize,
      includeNullInDescSort: true,
    });
    return activities;
  }

  async getActivitiesPaginated(
    filters?: Filters,
    sort?: Sort,
    nextCursor?: string,
    pageSize = 50
  ) {
    return this.apmClient.activities.list({
      filters: addSourceDataOnlyFilter(filters),
      sort,
      nextCursor,
      pageSize,
    });
  }

  async aggregateActivityByField(field: string, filters?: Filters) {
    const filter = addSourceDataOnlyFilter(filters);

    return this.apmClient.activities
      .aggregate({
        filters: filter,
        aggregates: [{ count: { property: field } }],
        groupBy: [field],
        searchOperator: 'AND',
      })
      .then((res) =>
        res.items.map((item) => ({
          value: String(item.group?.[field] || 'Unknown'),
          count: item.aggregates.find(isCountAggregate)?.value,
        }))
      );
  }

  async getActivitiesAggregatedByLocation(filters?: Filters) {
    const filter = addSourceDataOnlyFilter(filters);
    return this.apmClient.activities
      .aggregate({
        filters: filter,
        aggregates: [{ count: { property: 'mainAsset' } }],
        groupBy: ['mainAsset'],
        searchOperator: 'AND',
      })
      .then((res) =>
        res.items.map((item) => {
          const mainAsset = item.group?.mainAsset;
          return {
            field: String(
              (isNodeRef(mainAsset) && mainAsset.externalId) || 'Unknown'
            ),
            count: item.aggregates.find(isCountAggregate)?.value,
          };
        })
      );
  }

  async getOperations(filters?: Filters, sort?: Sort, limit = 500) {
    const { items: operations } = await this.apmClient.operations.list({
      filters: addSourceDataOnlyFilter(filters),
      sort,
      pageSize: limit,
    });
    return operations;
  }

  async getOperationsWithNextCursor(
    filters?: Filters,
    previousCursor?: string,
    limit = 500
  ) {
    const {
      items: operations,
      pageInfo: { nextCursor },
    } = await this.apmClient.operations.list({
      filters: addSourceDataOnlyFilter(filters),
      pageSize: limit,
      nextCursor: previousCursor,
    });
    return { operations, nextCursor };
  }

  async getOperationDetails(filters: Filters) {
    const {
      items: [operation],
    } = await this.apmClient.operations.list({
      filters: addSourceDataOnlyFilter(filters),
    });
    return operation;
  }
}

export const createCdmActivityService = (
  ...args: ConstructorParameters<typeof CdmActivityService>
): IActivityService => new CdmActivityService(...args);
